import GQL from '@lib/cms/gql';
import { COMMUNITY_QUERY } from '@lib/fragments';
import { buildCommunityHighlightFaqItems, resolveFaqContent } from '@lib/faq-content';
import { generateCommunitySchema } from '@lib/generate-schemas';
import { fetchAssociatedAgent, getAssociatedAgentItem } from '@lib/associatedAgent';
import communityFallbacks from '@src/data/community-fallbacks';
import Window from '@lib/window-size';
import { useLeadsPlusAgentData } from '@lib/hooks';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { fetchRecentAgentsByLocation } from '@lib/fetchRecentAgentsByLocation';
import { buildLocalEventsMeta, isEventUpcoming } from '@lib/utils';

// ---------------------------------------------------------

import Accordion from '@components/accordion';
import FaqBlock from '@components/faq-block';
import Callout from '@components/callout';
import Container from '@layout/container';
import Form from '@components/form';
import Image from '@components/image';
import Jumbotron from '@components/jumbotron';
import Layout from '@layout/index';
import TextBlock from '@components/text-block';
import TopicCarousel from '@components/topic-carousel';
import EnhancedContactForm from '@components/enhanced-form';
import FormRealtorCards from '@components/enhanced-form/realtor-cards';
import Grid from '@layout/grid';
import LifestyleCard from '@components/lifestyle-card';
import EventCard from '@components/event-card';
import TabSlider from '@components/tab-slider';
import Button from '@components/button';
import RecentAgentsGrid from '@components/recent-agents-grid';

// ---------------------------------------------------------

import {
  article_container,
  article_details,
  side_bar,
  summary,
  tabs,
  show_more
} from './styles.module.scss';

// ---------------------------------------------------------

export async function getServerSideProps(context) {
  const slug = context.params.slug;
  const previewEnv = context?.previewData?.environment || null;
  const preview = context.preview || false;
  const query = context?.query;
  let associatedAgent = null;

  const fallbackCommunity = communityFallbacks.find((community) => {
    return community?.fallbackSlugs?.includes(slug);
  });

  // redirect to the main community if the slug is a fallback community
  if (fallbackCommunity) {
    return {
      redirect: {
        destination: fallbackCommunity.slug,
        permanent: false
      }
    };
  }

  const data = await GQL.fetch(COMMUNITY_QUERY, { preview, slug }, preview, previewEnv);

  if (data.errors) {
    throw new Error(`Error fetching community static data: ${data.errors[0].message}`);
  }

  if (query.agentid) {
    const parsedAgentId = parseInt(query.agentid, 10);
    // Only attempt the lookup for a well-formed numeric id, and never let a
    // failed/invalid agent lookup take down the community page — fall back to
    // the standard (non-agent) experience and metadata instead.
    if (!Number.isNaN(parsedAgentId)) {
      try {
        associatedAgent = await fetchAssociatedAgent({ agentId: parsedAgentId });
      } catch (error) {
        console.error(`Error fetching associated agent ${parsedAgentId}:`, error);
        associatedAgent = null;
      }
    }
  }

  // handle if no data is returned for the inserted slug
  const pageData = data?.data?.datum?.items?.[0];

  if (!pageData) {
    return {
      notFound: true
    };
  }

  // ---------------------------------------------------------

  // first show non expired, with an expiration date
  // then show by community > metro which is already ordered
  function lifestyleRecordSort(arr) {
    const withExpiration = [];
    const withoutExpiration = [];
    arr.forEach((record) => {
      record?.expirationDate ? withExpiration.push(record) : withoutExpiration.push(record);
    });
    withExpiration.sort((a, b) => new Date(b.expirationDate) - new Date(a.expirationDate));
    return [...withExpiration, ...withoutExpiration];
  }

  const getLifestyleRecords = (pageData) => {
    const currentDateTime = new Date();

    const lifestyles = [
      ...(pageData?.linkedFrom?.lifestyleRecordCollection?.items ?? []),
      ...(pageData?.metro?.linkedFrom?.lifestyleRecordCollection?.items ?? [])
    ].filter((record) => record !== null && record !== undefined);

    const lifestyleRecords = lifestyles.filter((record) => {
      return (
        record.tags.includes('Community Pages') &&
        (!record?.expirationDate || new Date(record.expirationDate) > currentDateTime)
      );
    });

    // use the sys id to ensure uniqueness when using the sorted array
    const uniqueRecords = new Set();
    const sortedRecords = lifestyleRecordSort(lifestyleRecords).filter((record) => {
      if (uniqueRecords.has(record.sys.id)) {
        return false;
      } else {
        uniqueRecords.add(record.sys.id);
        return true;
      }
    });

    return sortedRecords;
  };

  const sortEvents = (events) => {
    const currentDate = new Date();

    return events.sort((a, b) => {
      // Convert dates to Date objects for comparison
      const aStartDate = new Date(a.eventDate);
      const aExpirationDate = new Date(a.expirationDate);
      const bStartDate = new Date(b.eventDate);
      const bExpirationDate = new Date(b.expirationDate);

      // Check if events are ongoing and haven't started yet
      const aUpcomingOngoing = aStartDate > currentDate && aExpirationDate > currentDate;
      const bUpcomingOngoing = bStartDate > currentDate && bExpirationDate > currentDate;

      // Prioritize upcoming ongoing events
      if (aUpcomingOngoing && !bUpcomingOngoing) return -1;
      if (!aUpcomingOngoing && bUpcomingOngoing) return 1;

      // Sort by expirationDate
      return aExpirationDate - bExpirationDate;
    });
  };

  const getEventRecords = (pageData) => {
    const currentDateTime = new Date();

    const events = [
      ...(pageData?.linkedFrom?.eventRecordCollection?.items ?? []),
      ...(pageData?.metro?.linkedFrom?.eventRecordCollection?.items ?? [])
    ].filter((record) => record !== null && record !== undefined);

    // Shared with the event JSON-LD so the tab, the FAQ count, and the schema agree.
    const eventRecords = events.filter((record) => isEventUpcoming(record, currentDateTime));

    // use the sys id to ensure uniqueness when using the sorted array
    const uniqueRecords = new Set();
    const sortedRecords = sortEvents(eventRecords).filter((record) => {
      if (uniqueRecords.has(record.sys.id)) {
        return false;
      } else {
        uniqueRecords.add(record.sys.id);
        return true;
      }
    });

    return sortedRecords;
  };

  // ---------------------------------------------------------

  // Fetch recent agents for this community
  // Parse location from slug (format: state-county-city, e.g., "oh-hamilton-cincinnati")
  const possibleFallback = communityFallbacks.find((x) => x.slug === slug);
  const slugs = possibleFallback?.fallbackSlugs?.map((x) => x) || [];
  slugs.push(slug);
  const allSlugVariations = [
    ...new Set([
      ...slugs,
      ...slugs
        .filter(
          (x) => x.includes('mt-') || x.includes('-twp') || x.includes('-st-') || x.includes('-hts')
        )
        .map((y) =>
          y
            .replace('mt-', 'mt.-')
            .replace('-twp', '-twp.')
            .replace('-st-', '-st.-')
            .replace('-hts', '-hts.')
        )
    ])
  ];

  let recentAgents = [],
    shouldContinue = true;
  while (shouldContinue) {
    recentAgents = await fetchRecentAgentsByLocation(getLocationFromSlug(allSlugVariations.pop()));
    if (recentAgents.length > 0) shouldContinue = false;
    if (allSlugVariations.length === 0) shouldContinue = false;
  }

  function getLocationFromSlug(slugWithLocation) {
    const slugParts = slugWithLocation.split('-');
    const state = slugParts[0]?.toUpperCase() || null;
    const cityRaw =
      slugParts.length > 2
        ? slugParts[2]
          ? slugParts.slice(2).join(' ')
          : null
        : slugParts[1] || null;
    const countyRaw = slugParts.length > 2 ? slugParts[1] || null : null;

    const city = cityRaw ? cityRaw : null;
    const county = countyRaw ? countyRaw : null;
    return { city, county, state };
  }

  // ---------------------------------------------------------

  // Build community structured data so crawlers identify the specific community
  // (name, location, region, nearby communities, listings, agents) rather than
  // the generic page module.
  const { city: cityRaw, county: countyRaw, state } = getLocationFromSlug(slug);
  // Capitalize the first letter of each word and each apostrophe-/hyphen-delimited
  // segment (so "o'fallon" -> "O'Fallon", "wright-patterson" -> "Wright-Patterson")
  // without lowercasing the rest, which would clobber internal capitals like
  // "McArthur".
  const toTitleCase = (value) =>
    value
      ? value.replace(
          /(^|[\s'’-])([a-z])/g,
          (_, boundary, letter) => boundary + letter.toUpperCase()
        )
      : null;
  const city = toTitleCase(cityRaw);
  const county = toTitleCase(countyRaw);

  const nearbyCommunitiesForSchema = (pageData.nearbyCommunities?.items || [])
    .map((item) => {
      if (!item?.slug) return null;

      if (item.__typename === 'Community') {
        return item.title ? { name: item.title, url: `/community/${item.slug}` } : null;
      }

      if (item.__typename === 'PageBuilderdevelopment') {
        const prefix =
          (item.category || 'Development') === 'Development' ? 'development' : 'builder';
        return item.builderDevelopmentName
          ? { name: item.builderDevelopmentName, url: `/${prefix}/${item.slug}` }
          : null;
      }

      return null;
    })
    .filter(Boolean);

  const communitySchema = generateCommunitySchema({
    title: pageData.title,
    slug,
    description: pageData.metaDescription || pageData.introductionText || pageData.summaryText,
    region: pageData.serviceArea || pageData.metro?.name,
    city,
    county,
    state,
    image:
      pageData.metaImage?.url || pageData.featuredImage?.url || pageData.headerImage?.url || null,
    listingsUrl: pageData.viewActivePropertiesLink,
    nearbyCommunities: nearbyCommunitiesForSchema,
    agents: recentAgents.map((agent) => ({ ...agent, name: agent.displayPrimary }))
  });

  const lifestyleData = getLifestyleRecords(pageData);
  const eventData = getEventRecords(pageData);

  // AEO Q&A. The VISIBLE block shows CMS-authored Q&A (faqSection) when present,
  // otherwise distinct highlights (nearby communities, listings, events, area) that
  // don't simply restate the section accordion. The FAQPage JSON-LD must mirror the
  // exact items the FaqBlock renders — Google's structured-data policy forbids
  // marking up content that isn't visible on the page.
  const { items: faqItems, schema: faqSchema } = resolveFaqContent(
    pageData?.faqSection,
    buildCommunityHighlightFaqItems(pageData, { events: eventData })
  );

  // Resolve the associated agent once so both the sidebar form and the
  // Local Events metadata below use the same (validated) record.
  const associatedAgentItem = getAssociatedAgentItem(associatedAgent);

  // Local Events tab metadata. When a link to the Local Events tab is shared,
  // the metadata should describe the events content (optionally attributing an
  // associated agent) rather than reusing the community-wide About metadata.
  // Computed server-side from the `tab` query param so it is present in the
  // initial HTML for search engines and social scrapers. This applies even when
  // there are currently no published events — it describes the page, not that
  // events are guaranteed to be available.
  // Only attribute an agent when we resolved a valid, named agent record;
  // otherwise buildLocalEventsMeta falls back to the standard non-agent copy.
  const localEventsMeta = buildLocalEventsMeta({
    tab: query?.tab,
    communityName: pageData.title,
    agentName: associatedAgentItem?.displayPrimary
  });

  return {
    props: {
      data: pageData || {},
      faqItems,
      faqSchema,
      lifestyleData,
      eventData,
      associatedAgent: associatedAgentItem,
      localEventsMeta,
      recentAgents,
      communitySchema,
      preview
    }
  };
}

const CommunityPage = ({
  data,
  faqItems,
  faqSchema,
  preview,
  lifestyleData,
  eventData,
  associatedAgent: associatedAgentProp,
  localEventsMeta,
  recentAgents,
  communitySchema
}) => {
  const router = useRouter();
  const mediaIsMedium = Window().width < 1040;
  const [selectedTab, setSelectedTab] = useState('About');

  const {
    leadsPlusAgent,
    setLeadsPlusAgent,
    setLeadsPlusAgentToSessionStorage,
    getLeadsPlusAgentFromSessionStorage
  } = useLeadsPlusAgentData(associatedAgentProp);

  const carouselSlides =
    data.nearbyCommunities?.items?.map((item) => ({
      description: item?.excerpt || item?.summaryText || item?.introductionText,
      featuredImage: item?.featuredImage || {
        height: 960,
        url: 'https://images.ctfassets.net/wzzep3ntdgx4/4V3cWpoV2LctBsJYc6SnSf/4ef45ecd14a78222b508078d9fe9d345/AdobeStock_376819599.jpeg',
        width: 1440
      },
      linkLabel: item?.linkLabel || 'View Community',
      linkUrl:
        item?.__typename === 'Community'
          ? `/community/${item?.slug}`
          : `/development/${item?.slug}`,
      title: item?.title || item?.builderDevelopmentName
    })) || [];

  const contactForm = data.contactForm;

  const sectionNames = ['Transportation', 'Hospitals', 'Shopping', 'Recreation'];

  const getCommunityData = () => {
    let arr = [];

    sectionNames.map((section) => {
      const sectionId = section.toLowerCase() + 'Text';
      const sectionText = data[sectionId];

      if (sectionText) {
        arr.push({
          body: (
            <div>
              {sectionText.split('\n').map((paragraph, i) => (
                <p key={i}>{paragraph}</p>
              ))}
            </div>
          ),
          id: section,
          label: section
        });
      }
    });

    if (lifestyleData?.length > 0) {
      arr.push({
        body: (
          <div>
            <p>Check out these local highlights by area experts!</p>
            <Grid layout="2">
              {lifestyleData?.slice(0, 4).map((item, index) => (
                <LifestyleCard
                  key={index}
                  headline={item.headline}
                  authorName={item?.authorName}
                  linkLabel={item?.linkLabel}
                  linkUrl={item.linkUrl}
                  theme={item.theme}
                  featuredImage={item.featuredImage?.url}
                ></LifestyleCard>
              ))}
            </Grid>
          </div>
        ),
        id: 'Lifestyle',
        label: 'Lifestyle'
      });
    }

    return arr;
  };

  const LIMIT = 8;

  const [eventsShown, setEventsShown] = useState(LIMIT);
  const events = eventData?.slice(0, eventsShown);
  const loadMore = () => {
    setEventsShown(eventsShown + LIMIT);
  };

  useEffect(() => {
    if (router.query.tab) {
      if (router.query.tab === 'local-events' && eventData.length > 0) {
        setSelectedTab('Local Events');
      }
    }
  }, [router]);

  const handleSwitchTabs = useCallback(
    (newTab) => {
      const newQuery = {
        ...router.query,
        tab: newTab.toLowerCase().replace(' ', '-')
      };
      router.replace({ query: newQuery });
      setSelectedTab(newTab);
    },
    [router, setSelectedTab]
  );

  const routerDefaultTab = useMemo(() => {
    if (router.query.tab) {
      if (router.query.tab === 'local-events' && eventData.length > 0) {
        return 'Local Events';
      }
    }
    return 'About';
  }, [router]);

  useEffect(() => {
    // set associated agent data if it exists in session storage
    // for a case where the user comes from a page with an agentid in the query
    const agentData = getLeadsPlusAgentFromSessionStorage();
    if (agentData && !associatedAgentProp) {
      setLeadsPlusAgent(agentData);
    }
  }, [router, associatedAgentProp, getLeadsPlusAgentFromSessionStorage, setLeadsPlusAgent]);

  useEffect(() => {
    // set agent data to session storage if it was provided by the server
    if (associatedAgentProp) {
      setLeadsPlusAgentToSessionStorage(associatedAgentProp);
    }
  }, [associatedAgentProp, setLeadsPlusAgentToSessionStorage]);

  // Prepare event schema data if events exist
  const eventSchemaData =
    eventData?.length > 0
      ? {
          events: eventData
        }
      : null;

  return (
    <Layout
      meta={{
        description: localEventsMeta?.description || data.metaDescription || data.introductionText,
        image: data.metaImage || data.featuredImage,
        title: localEventsMeta?.title || data.title
      }}
      preview={preview}
      specializedPageSchema={eventSchemaData ? 'communityEventSchema' : undefined}
      eventSchemaData={eventSchemaData}
      // The FaqBlock renders only on the About tab, so only emit its schema there —
      // a crawler landing on ?tab=local-events must not get FAQ markup with no
      // matching visible FAQ.
      faqSchema={selectedTab === 'About' ? faqSchema : undefined}
      communitySchema={communitySchema}
    >
      {/* JUMBOTRON SECTION */}
      <Jumbotron
        body={data.serviceArea}
        ctas={
          data.viewActivePropertiesLink
            ? [
                {
                  label: 'View Active Properties',
                  theme: 'red',
                  url: data.viewActivePropertiesLink
                }
              ]
            : []
        }
        theme="centered"
        image={{
          height: '500px',
          url:
            data.headerImage?.url ||
            'https://images.ctfassets.net/wzzep3ntdgx4/B2zZsgoajAu2j4IvSpp9h/1baff99d5c5134b3fe8e014a747f2075/IMG_8039.jpg',
          width: '1440px'
        }}
        title={data.title}
      />

      {/* COMMUNITY BODY SECTIONS */}
      <Container removeBottomSpacing spacing="medium" theme="background-is-white">
        <div className={article_container}>
          <div className={article_details}>
            <div className={tabs}>
              <TabSlider
                items={[{ label: 'About' }, eventData.length > 0 && { label: 'Local Events' }]}
                theme="unified"
                defaultSelection={routerDefaultTab}
                setSelectedTabCallback={handleSwitchTabs}
              ></TabSlider>
            </div>
            {selectedTab === 'About' && (
              <>
                <div className={summary}>
                  {data.introductionText &&
                    data.introductionText
                      .split('\n')
                      .map((paragraph, i) => <p key={i}>{paragraph}</p>)}
                  {data.summaryText &&
                    data.summaryText.split('\n').map((paragraph, i) => <p key={i}>{paragraph}</p>)}
                </div>
                <Accordion
                  listItems={{ items: getCommunityData() }}
                  openAccordionId={['Lifestyle']}
                ></Accordion>
                <FaqBlock
                  title={`Frequently Asked Questions About ${data.title}`}
                  items={faqItems}
                />
              </>
            )}
            {eventData.length > 0 && selectedTab === 'Local Events' && (
              <div>
                <p>Stay up to date with local events!</p>
                <Grid layout="1">
                  {events.map((item, index) => (
                    <EventCard
                      key={index}
                      headline={item.headline}
                      authorName={item?.authorName}
                      linkLabel={item?.linkLabel}
                      linkUrl={item.linkUrl}
                      theme={item.theme}
                      featuredImage={item.featuredImage?.url}
                      schedule={item.schedule}
                      eventDate={item.eventDate}
                      expirationDate={item.expirationDate}
                      eventDetails={item.eventDetails}
                    ></EventCard>
                  ))}
                </Grid>
                {eventData.length > LIMIT && (
                  <div className={show_more}>
                    {eventsShown < eventData.length - 1 && (
                      <Button label="Show More" onClick={loadMore}></Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {(data.sidebar || leadsPlusAgent) && (
            <div className={side_bar}>
              {data.sidebar?.__typename && (
                <Callout {...data.sidebar} placement="sidebar" theme="keyline" />
              )}
              {leadsPlusAgent && (
                <EnhancedContactForm
                  realtors={[leadsPlusAgent]}
                  sticky={!mediaIsMedium}
                  requestType={['Community Page', 'Leads Plus']}
                  agentIds={[leadsPlusAgent.agentId]}
                >
                  <FormRealtorCards realtors={[leadsPlusAgent]} />
                </EnhancedContactForm>
              )}
            </div>
          )}
        </div>
      </Container>

      {/* EDUCATION SECTION */}
      {data.schools?.items?.map((school, idx) => {
        return (
          <Container key={`school-${idx}`} layout="half" theme="background-is-white" width="large">
            <TextBlock
              content={school.description}
              link={{
                aria: `Learn more about ${data.serviceArea} Schools`,
                icon: true,
                label: `Explore ${data.serviceArea} Schools`,
                url: school.slug || '#'
              }}
              title="Education"
            />
            <Image
              image={{
                alt: school.image?.alt,
                height: 310,
                src: school.image?.url
              }}
            />
          </Container>
        );
      })}

      {/* NEARBY COMMUNITIES SECTION */}
      {data.nearbyCommunities?.items?.length > 0 && (
        <Container layout="single" theme="background-is-gray">
          <TopicCarousel
            topic="community"
            carouselHeader="Explore Nearby Communities"
            slidesCollection={{
              cards: carouselSlides
            }}
            theme="full-width"
          />
        </Container>
      )}

      {/* RECENT AGENTS SECTION */}
      {recentAgents && recentAgents.length > 0 && !leadsPlusAgent && (
        <Container spacing="medium" theme="background-is-white">
          <RecentAgentsGrid
            agents={recentAgents}
            heading={`Connect With an Agent Who Recently Listed or Sold in ${data.title}`}
          />
        </Container>
      )}

      {/* METRO ACCORDION SECTION */}
      {data.metro?.accordion?.listItems?.items?.length > 0 && (
        <Container
          header={data.metro?.name}
          padding="large"
          width="medium"
          textAlignment="center"
          theme="background-is-white"
        >
          <Accordion theme="red" listItems={data.metro?.accordion?.listItems} />
        </Container>
      )}

      {/* CONTACT FORM SECTION */}
      {contactForm && <Form {...contactForm} />}
    </Layout>
  );
};

export default CommunityPage;
